import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useUsers } from '../../queries/users';
import {
  computeDailySignups,
  computeUserStats,
  EMPTY_USER_FILTERS,
  filterUsers,
  subscriptionLevels,
} from './users-helpers';
import { UserStatsCards } from './UserStatsCards';
import { UserSignupsChart } from './UserSignupsChart';
import { UsersTable } from './UsersTable';
import { PageHeader } from '../../components/ui/PageHeader';
import { Spinner } from '../../components/ui/Spinner';

const INPUT = 'h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function UsersListPage() {
  const { data: users, isLoading, error } = useUsers();
  const rows = users ?? [];

  const [filters, setFilters] = useState(EMPTY_USER_FILTERS);
  const levels = useMemo(() => subscriptionLevels(rows), [rows]);
  const filtered = useMemo(() => filterUsers(rows, filters), [rows, filters]);

  return (
    <div className="space-y-6">
      <PageHeader title="Users" description="Registered users, signups and marketing consent." />

      {isLoading ? (
        <Spinner label="Loading user data…" />
      ) : error ? (
        <div className="py-8 text-center text-destructive">
          {error instanceof Error ? error.message : 'Failed to load users.'}
        </div>
      ) : (
        <>
          <UserStatsCards stats={computeUserStats(rows)} />
          <UserSignupsChart dailySignups={computeDailySignups(rows)} />

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">Registered Users</h2>
              <span className="text-sm text-muted-foreground">
                {filtered.length === rows.length
                  ? `${rows.length.toLocaleString()} users`
                  : `${filtered.length.toLocaleString()} of ${rows.length.toLocaleString()} users`}
              </span>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="relative flex-1 sm:min-w-[16rem] sm:max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                  placeholder="Search by name, email, phone or ID…"
                  className={`${INPUT} w-full pl-9`}
                />
              </div>
              <select
                value={filters.subscription}
                onChange={(e) => setFilters((f) => ({ ...f, subscription: e.target.value }))}
                className={INPUT}
                aria-label="Subscription level"
              >
                <option value="">All subscriptions</option>
                {levels.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
              <select
                value={filters.marketing}
                onChange={(e) => setFilters((f) => ({ ...f, marketing: e.target.value as typeof f.marketing }))}
                className={INPUT}
                aria-label="Marketing consent"
              >
                <option value="all">Marketing: all</option>
                <option value="yes">Marketing: yes</option>
                <option value="no">Marketing: no</option>
              </select>
              <select
                value={filters.weekly}
                onChange={(e) => setFilters((f) => ({ ...f, weekly: e.target.value as typeof f.weekly }))}
                className={INPUT}
                aria-label="Weekly tips"
              >
                <option value="all">Weekly tips: all</option>
                <option value="yes">Weekly tips: yes</option>
                <option value="no">Weekly tips: no</option>
              </select>
            </div>

            <UsersTable users={filtered} />
          </div>
        </>
      )}
    </div>
  );
}
